import { toast } from 'sonner';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import { useTimelineSettingsStore } from '@/features/timeline/stores/timeline-settings-store';
import { useProjectStore } from '@/features/projects/stores/project-store';
import { usePlaybackStore } from '@/features/preview/stores/playback-store';
import type { TimelineItem, TimelineTrack } from '@/types/timeline';

type ReactionTemplateId = 'corner_sticker' | 'bottom_pop' | 'center_punch';
type ReactionTiming = 'scene_start' | 'scene_middle' | 'scene_end';
type ReactionMediaType = 'image' | 'gif' | 'mp4';
type BatchMode = 'light' | 'impact';

type ReactionAsset = {
  id: string;
  name: string;
  mediaType: ReactionMediaType;
  sourceUrl: string;
  previewUrl?: string;
  recommendedDurationMs?: number;
  safeTemplates?: ReactionTemplateId[];
};

type InsertReactionArgs = {
  asset: ReactionAsset;
  templateId: ReactionTemplateId;
  timing: ReactionTiming;
};

const REACTION_TRACK_ID = 'reaction-overlay-track';
const REACTION_ORIGIN_PREFIX = 'reaction-overlay';

const makeId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;

const getFileName = (url: string) => {
  try {
    return decodeURIComponent(url.split('/').pop()?.split('?')[0] || 'reaction-asset');
  } catch {
    return 'reaction-asset';
  }
};

const isReactionItem = (item: TimelineItem) =>
  String(item.originId || '').startsWith(REACTION_ORIGIN_PREFIX);

const findSceneItemAtFrame = (items: TimelineItem[], frame: number) => {
  const sceneLikeItems = items
    .filter((item) => item.type === 'video' || item.type === 'image' || item.type === 'composition')
    .filter((item) => item.label?.startsWith('Scene'))
    .sort((a, b) => a.from - b.from);

  const activeScene = sceneLikeItems.find((item) => frame >= item.from && frame < item.from + item.durationInFrames);
  if (activeScene) return activeScene;

  return sceneLikeItems.find((item) => item.from >= frame) || sceneLikeItems.at(-1) || null;
};

const resolveOverlayOrder = (tracks: TimelineTrack[], items: TimelineItem[]) => {
  const visualTrackIds = new Set(
    items
      .filter((item) => item.type === 'video' || item.type === 'image' || item.type === 'composition')
      .map((item) => item.trackId)
  );

  const visualOrders = tracks
    .filter((track) => visualTrackIds.has(track.id))
    .map((track) => track.order ?? 0);

  if (visualOrders.length > 0) {
    const minVisualOrder = Math.min(...visualOrders);
    return minVisualOrder - 0.5;
  }

  return Math.max(...tracks.map((track) => track.order ?? 0), 0) + 1;
};

const ensureReactionTrack = (tracks: TimelineTrack[], items: TimelineItem[]) => {
  const existing = tracks.find((track) => track.id === REACTION_TRACK_ID);
  const overlayOrder = resolveOverlayOrder(tracks, items);
  if (existing) {
    return {
      tracks: tracks
        .map((track) => track.id === REACTION_TRACK_ID ? { ...track, order: overlayOrder } : track)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      trackId: existing.id,
    };
  }

  const nextTracks = tracks.map((track) => ({
    ...track,
    items: [...(track.items || [])],
  }));

  nextTracks.push({
    id: REACTION_TRACK_ID,
    name: 'Reaction Overlays',
    order: overlayOrder,
    height: 42,
    locked: false,
    visible: true,
    muted: false,
    solo: false,
    items: [],
  });

  return {
    tracks: nextTracks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    trackId: REACTION_TRACK_ID,
  };
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const toTransform = (
  width: number,
  height: number,
  rect: { left: number; top: number; width: number; height: number; fit?: MediaFitMode; radius?: number }
) => ({
  x: Math.round(rect.left + rect.width / 2 - width / 2),
  y: Math.round(rect.top + rect.height / 2 - height / 2),
  width: Math.round(rect.width),
  height: Math.round(rect.height),
  objectFit: rect.fit || 'contain',
  cornerRadius: rect.radius ?? 24,
  opacity: 1,
  rotation: 0,
});

const resolveTemplateRect = (
  templateId: ReactionTemplateId,
  canvasWidth: number,
  canvasHeight: number,
  mediaType: ReactionMediaType
) => {
  const isVertical = canvasHeight >= canvasWidth;

  if (templateId === 'corner_sticker') {
    return toTransform(canvasWidth, canvasHeight, {
      left: canvasWidth * 0.36,
      top: canvasHeight * 0.06,
      width: canvasWidth * (isVertical ? 0.48 : 0.34),
      height: canvasHeight * (isVertical ? 0.32 : 0.42),
      fit: 'contain',
      radius: 18,
    });
  }

  if (templateId === 'center_punch') {
    return toTransform(canvasWidth, canvasHeight, {
      left: canvasWidth * 0.04,
      top: canvasHeight * 0.06,
      width: canvasWidth * 0.92,
      height: canvasHeight * (mediaType === 'mp4' ? 0.46 : 0.4),
      fit: 'contain',
      radius: 30,
    });
  }

  return toTransform(canvasWidth, canvasHeight, {
    left: canvasWidth * 0.04,
    top: canvasHeight * 0.54,
    width: canvasWidth * 0.92,
    height: canvasHeight * (mediaType === 'mp4' ? 0.36 : 0.32),
    fit: mediaType === 'mp4' ? 'cover' : 'contain',
    radius: 24,
  });
};

const resolveDurationInFrames = (
  asset: ReactionAsset,
  templateId: ReactionTemplateId,
  fps: number,
  sceneDurationInFrames: number
) => {
  const defaultMs = templateId === 'center_punch' ? 1400 : templateId === 'corner_sticker' ? 1800 : 2200;
  const effectiveMs = Math.max(asset.recommendedDurationMs || 0, defaultMs);
  const adjustedMs = asset.mediaType === 'gif' ? Math.round(effectiveMs * 0.5) : effectiveMs;
  const rawFrames = Math.round((adjustedMs / 1000) * fps);
  return clamp(
    rawFrames,
    Math.round(fps * 1.0),
    Math.max(Math.round(fps * 1.0), Math.min(sceneDurationInFrames, Math.round(fps * 2.8)))
  );
};

const resolveFromFrame = (
  timing: ReactionTiming,
  sceneStart: number,
  sceneDuration: number,
  overlayDuration: number
) => {
  if (timing === 'scene_start') return sceneStart;
  if (timing === 'scene_end') return Math.max(sceneStart, sceneStart + sceneDuration - overlayDuration);
  return Math.max(sceneStart, sceneStart + Math.round((sceneDuration - overlayDuration) / 2));
};

export const insertReactionOverlay = ({ asset, templateId, timing }: InsertReactionArgs) => {
  const itemsStore = useItemsStore.getState();
  const settingsStore = useTimelineSettingsStore.getState();
  const playbackStore = usePlaybackStore.getState();
  const currentProject = useProjectStore.getState().currentProject;
  const fps = settingsStore.fps || currentProject?.metadata?.fps || 30;
  const width = currentProject?.metadata?.width || 1080;
  const height = currentProject?.metadata?.height || 1920;
  const currentFrame = playbackStore.currentFrame;

  const sceneItem = findSceneItemAtFrame(itemsStore.items, currentFrame);
  if (!sceneItem) {
    toast.error('현재 playhead 기준으로 Scene을 찾지 못했습니다.');
    return false;
  }

  const { tracks: nextTracks, trackId } = ensureReactionTrack(itemsStore.tracks, itemsStore.items);
  const durationInFrames = resolveDurationInFrames(asset, templateId, fps, sceneItem.durationInFrames);
  const from = resolveFromFrame(timing, sceneItem.from, sceneItem.durationInFrames, durationInFrames);
  const transform = resolveTemplateRect(templateId, width, height, asset.mediaType);
  const label = getFileName(asset.sourceUrl);

  const baseItem = {
    id: makeId('reaction'),
    trackId,
    from,
    durationInFrames,
    label,
    mediaId: asset.sourceUrl,
    originId: `${REACTION_ORIGIN_PREFIX}:${templateId}:${asset.id}`,
    transform,
  };

  const item: TimelineItem = asset.mediaType === 'mp4'
    ? {
        ...baseItem,
        type: 'video',
        src: asset.sourceUrl,
        volume: -60,
      }
    : {
        ...baseItem,
        type: 'image',
        src: asset.sourceUrl,
      };

  const mergedItems = [...itemsStore.items, item].sort((a, b) => a.from - b.from);
  const syncedTracks = nextTracks.map((track) => ({
    ...track,
    items: mergedItems.filter((entry) => entry.trackId === track.id),
  }));

  useItemsStore.getState().setTracks(syncedTracks);
  useItemsStore.getState().setItems(mergedItems);
  useTimelineSettingsStore.getState().markDirty();
  toast.success(`${asset.name} 반응을 씬에 넣었습니다.`);
  return true;
};

const clearReactionItemsFromState = (tracks: TimelineTrack[], items: TimelineItem[]) => {
  const remainingItems = items.filter((item) => !isReactionItem(item));
  const remainingTracks = tracks
    .filter((track) => track.id !== REACTION_TRACK_ID || remainingItems.some((item) => item.trackId === track.id))
    .map((track) => ({
      ...track,
      items: remainingItems.filter((item) => item.trackId === track.id),
    }));

  return {
    tracks: remainingTracks,
    items: remainingItems,
  };
};

export const clearReactionOverlays = () => {
  const itemsStore = useItemsStore.getState();
  const cleaned = clearReactionItemsFromState(itemsStore.tracks, itemsStore.items);
  useItemsStore.getState().setTracks(cleaned.tracks);
  useItemsStore.getState().setItems(cleaned.items);
  useTimelineSettingsStore.getState().markDirty();
};

const deriveSceneText = (sceneItem: TimelineItem, items: TimelineItem[]) => {
  const sceneEnd = sceneItem.from + sceneItem.durationInFrames;
  return items
    .filter((item) => item.type === 'text' && typeof item.text === 'string')
    .filter((item) => item.from < sceneEnd && item.from + item.durationInFrames > sceneItem.from)
    .sort((a, b) => a.from - b.from)
    .map((item) => item.text)
    .join(' ')
    .trim()
    .toLowerCase();
};

const classifyEmotion = (text: string): ReactionAsset['emotionTag'] => {
  if (!text) return 'funny';
  if (/(충격|헉|미쳤|소름|설마|끔찍|잔혹|정체)/.test(text)) return 'shock';
  if (/(위험|경고|조심|금지|큰일|불꽃|전쟁|군사|잔혹)/.test(text)) return 'warning';
  if (/(분노|빡|화가|때려|먹어줄게|손모가지)/.test(text)) return 'angry';
  if (/(왜|의문|뭐지|생각|추악|진실)/.test(text)) return 'awkward';
  if (/(봐|바로|포인트|핵심|정답)/.test(text)) return 'point';
  if (/(귀엽|예쁘|꽃|하트)/.test(text)) return 'cute';
  return 'funny';
};

const chooseTemplateForBatch = (emotion: ReactionAsset['emotionTag'], mode: BatchMode): ReactionTemplateId => {
  if (mode === 'impact') {
    if (emotion === 'warning' || emotion === 'shock' || emotion === 'angry') return 'center_punch';
    return 'bottom_pop';
  }
  if (emotion === 'cute' || emotion === 'awkward') return 'corner_sticker';
  return 'bottom_pop';
};

const chooseTimingForBatch = (templateId: ReactionTemplateId, mode: BatchMode): ReactionTiming => {
  if (templateId === 'center_punch') return 'scene_middle';
  if (mode === 'impact') return 'scene_middle';
  return 'scene_end';
};

const chooseAssetForBatch = (
  assets: ReactionAsset[],
  emotion: ReactionAsset['emotionTag'],
  templateId: ReactionTemplateId,
  mode: BatchMode,
  usedAssetIds: Set<string>,
  usedMp4Count: number
) => {
  const candidates = assets.filter((asset) => {
    const matchesEmotion = asset.emotionTag === emotion || (mode === 'light' && emotion === 'funny');
    const matchesTemplate = !asset.safeTemplates?.length || asset.safeTemplates.includes(templateId);
    const avoidsHeavy = mode === 'light' ? !asset.isHeavy && asset.mediaType !== 'mp4' : true;
    const mp4Allowed = asset.mediaType !== 'mp4' || usedMp4Count < 2;
    return matchesEmotion && matchesTemplate && avoidsHeavy && mp4Allowed;
  });

  const sorted = [...candidates].sort((a, b) => {
    const score = (asset: ReactionAsset) => {
      let result = 0;
      if (!usedAssetIds.has(asset.id)) result += 2;
      if (asset.mediaType === 'image') result += mode === 'light' ? 2 : 0;
      if (asset.mediaType === 'gif') result += 1;
      if (asset.mediaType === 'mp4') result += mode === 'impact' ? 2 : -5;
      return result;
    };
    return score(b) - score(a);
  });

  return sorted[0] || null;
};

export const batchInsertReactionOverlays = (assets: ReactionAsset[], mode: BatchMode) => {
  const itemsStore = useItemsStore.getState();
  const settingsStore = useTimelineSettingsStore.getState();
  const currentProject = useProjectStore.getState().currentProject;
  const fps = settingsStore.fps || currentProject?.metadata?.fps || 30;

  const sceneItems = itemsStore.items
    .filter((item) => item.label?.startsWith('Scene'))
    .filter((item) => item.type === 'video' || item.type === 'image' || item.type === 'composition')
    .sort((a, b) => a.from - b.from);

  if (sceneItems.length === 0) {
    toast.error('배치할 Scene이 없습니다.');
    return false;
  }

  const cleaned = clearReactionItemsFromState(itemsStore.tracks, itemsStore.items);
  const { tracks: nextTracks, trackId } = ensureReactionTrack(cleaned.tracks, cleaned.items);
  const nextItems = [...cleaned.items];
  const usedAssetIds = new Set<string>();
  let usedMp4Count = 0;
  let placedCount = 0;
  const maxPlacements = mode === 'impact' ? 5 : 3;

  sceneItems.forEach((sceneItem, index) => {
    if (placedCount >= maxPlacements) return;

    const sceneText = deriveSceneText(sceneItem, cleaned.items);
    const emotion = classifyEmotion(sceneText);
    const templateId = chooseTemplateForBatch(emotion, mode);

    // Light mode leaves more breathing room.
    if (mode === 'light' && index % 2 === 1 && emotion === 'funny') {
      return;
    }

    const asset = chooseAssetForBatch(assets, emotion, templateId, mode, usedAssetIds, usedMp4Count);
    if (!asset) return;

    const durationInFrames = resolveDurationInFrames(asset, templateId, fps, sceneItem.durationInFrames);
    const timing = chooseTimingForBatch(templateId, mode);
    const from = resolveFromFrame(timing, sceneItem.from, sceneItem.durationInFrames, durationInFrames);
    const width = currentProject?.metadata?.width || 1080;
    const height = currentProject?.metadata?.height || 1920;
    const transform = resolveTemplateRect(templateId, width, height, asset.mediaType);
    const label = getFileName(asset.sourceUrl);

    const baseItem = {
      id: makeId('reaction'),
      trackId,
      from,
      durationInFrames,
      label,
      mediaId: asset.sourceUrl,
      originId: `${REACTION_ORIGIN_PREFIX}:${templateId}:${asset.id}`,
      transform,
    };

    const item: TimelineItem = asset.mediaType === 'mp4'
      ? {
          ...baseItem,
          type: 'video',
          src: asset.sourceUrl,
          volume: -60,
        }
      : {
          ...baseItem,
          type: 'image',
          src: asset.sourceUrl,
        };

    nextItems.push(item);
    usedAssetIds.add(asset.id);
    if (asset.mediaType === 'mp4') usedMp4Count += 1;
    placedCount += 1;
  });

  if (placedCount === 0) {
    toast.error('자동으로 배치할 반응을 찾지 못했습니다.');
    return false;
  }

  const mergedItems = nextItems.sort((a, b) => a.from - b.from);
  const syncedTracks = nextTracks.map((track) => ({
    ...track,
    items: mergedItems.filter((item) => item.trackId === track.id),
  }));

  useItemsStore.getState().setTracks(syncedTracks);
  useItemsStore.getState().setItems(mergedItems);
  useTimelineSettingsStore.getState().markDirty();
  toast.success(mode === 'impact' ? `반응 ${placedCount}개를 임팩트 있게 배치했습니다.` : `반응 ${placedCount}개를 가볍게 배치했습니다.`);
  return true;
};
import type { MediaFitMode } from '@/types/transform';
